import { FC, Fragment, useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import { Formik } from "formik";
import { t } from "i18next";
import * as Yup from "yup";

import { faCartShopping } from "@fortawesome/pro-regular-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

import { selectUser } from "@/app/auth/auth.slice";
import {
  useCartsQuery,
  useCreateCartMutation,
  useDeleteCartMutation,
  useUpdateCartMutation,
} from "@/app/carts/carts.api";
import {
  useLazyStripeSetupIntentQuery,
  usePaymentConfigQuery,
} from "@/app/payments/payments.api";
import {
  useCategoriesQuery,
  useProductsStoreQuery,
} from "@/app/products/products.api";
import { InputWithCornerHint } from "@/shared/components/atoms/InputWithCornerHint";
import { InputWithSelectHint } from "@/shared/components/atoms/InputWithSelectHint";
import { InputWithLinkNextToIt } from "@/shared/components/atoms/InputWithLinkNextToIt";
import { DescriptionListCart } from "@/shared/components/molecules/DescriptionListCart";
import { DialogCreate } from "@/shared/components/molecules/DialogCreate";
import { ListCart } from "@/shared/components/molecules/ListCart";
import { MultiSelect } from "@/shared/components/organisms/MultiSelect";
import { PaymentForm } from "@/shared/components/organisms/PaymentForm";
import Product from "@/shared/components/organisms/ProductTienda";
import { Select } from "@/shared/components/organisms/Select";
import { Empty } from "@/shared/components/organisms/Empty";

import { useDebounce } from "@/shared/hooks/debounce";
import { toasts } from "@/shared/utils/toasts";

import { useFetchUsersMutation } from "@/app/users/users.api";

import { useBuyMutation } from "@/app/store/store.api";

import { useSepaDebitPaymentMutation } from "@/app/payments/payments.api";
import { setPaymentAction } from "@/app/payments/payment.slice";

export const UserStore: FC<{
  onOpenStock: () => void;
  id: number;
  user_current: any;
}> = ({ onOpenStock, id, user_current }) => {
  // const stripe = useStripe();
  const dispatch = useDispatch();
  const user = useSelector(selectUser);
  const [productSearch, setProductSearch] = useState("");
  const debouncedProductSearch = useDebounce(productSearch, 200);

  const [categories, setCategories] = useState([]);
  const [cartVisible, setCartVisible] = useState(false);
  const [selectedUser, setSelectedUser] = useState(undefined);
  const [currentUser, setCurrentUser] = useState<undefined>(user_current);

  useEffect(() => {
    if (user_current) {
      setCurrentUser(user_current);
    }
  }, [user_current]);

  const [fetchUsers, { isError, isLoading }] = useFetchUsersMutation();

  const paymentConfigQuery = usePaymentConfigQuery({ id });
  const productsQuery = useProductsStoreQuery({
    branchId: id,
    search: debouncedProductSearch,
    categoryIds: categories.map((category: any) => category.id),
    minAmount: 0,
  });
  const categoriesQuery = useCategoriesQuery({ branchId: id });

  const { data: cartsData, refetch: refetchCarts } = useCartsQuery({
    branchId: id,
    userId: user?.id,
  });

  const [createCart, createCartResult] = useCreateCartMutation();
  const [updateCart, updateCartResult] = useUpdateCartMutation();
  const [deleteCart, deleteCartResult] = useDeleteCartMutation();
  const [createBuyStore, stateBuyStore] = useBuyMutation();

  // Payments process
  const [stripeSetupIntent, stripeSetupIntentResult] =
    useLazyStripeSetupIntentQuery();

  const [sepaDebitPayment, sepaDebitPaymentResult] =
    useSepaDebitPaymentMutation();

  useEffect(() => {
    if (currentUser) {
      stripeSetupIntent({ userId: currentUser?.id, paymentMethodType: "card" });
    }
  }, [currentUser]);

  useEffect(() => {
    if (createCartResult.isSuccess) {
      toasts.success(
        "Producto agregado",
        "El producto fué agregado al carrito de la compra exitosamente"
      );
    }

    if (createCartResult.isError) {
      toasts.danger(
        "Producto no agregado",
        createCartResult.error?.data?.data
          ? createCartResult.error?.data?.data
          : "Ocurrió un error al agregar el producto al carrito de la compra"
      );
    }
  }, [createCartResult]);

  useEffect(() => {
    if (updateCartResult.isSuccess) {
      toasts.success(
        "Cambios realizados",
        "El producto fué actualizado exitosamente"
      );
    }

    if (updateCartResult.isError) {
      toasts.danger(
        "Cambios no realizados",
        "Ocurrió un error el actualizar el carrito de la compra"
      );
    }
  }, [updateCartResult]);

  useEffect(() => {
    if (deleteCartResult.isSuccess) {
      toasts.success(
        "Cambios realizados",
        "El producto fué eliminado del carrito de la compra exitosamente"
      );
    }

    if (deleteCartResult.isError) {
      toasts.danger(
        "Cambios no realizados",
        "Ocurrió un error al eliminar el producto del carrito de la compra"
      );
    }
  }, [deleteCartResult]);

  const total = useMemo(
    () =>
      cartsData?.reduce(
        (total: number, cart: any) =>
          total + parseFloat(cart.price) * cart.amount,
        0
      ) ?? 0,
    [cartsData]
  );

  useEffect(() => {
    setItemCart(cartsData?.length ?? 0);
  }, [cartsData]);

  const handleOpenCart = () => {
    setCartVisible(!cartVisible);
  };

  const optionsHacienda = [
    { id: 1, name: t("gti.views.not_generate") },
    { id: 2, name: t("accounting.views.ticket") },
    { id: 3, name: t("accounting.views.invoice") },
  ];

  const [selectHacienda, setSelectHacienda] = useState(optionsHacienda[0]);
  const optionId = [
    { id: 1, name: t("store.views.cedula_fisica") },
    { id: 2, name: t("store.views.cedula_juridica") },
    { id: 3, name: t("store.views.dimex") },
    { id: 4, name: t("store.views.nite") },
    { id: 5, name: t("store.views.extranjera") },
  ];
  const [selectOptionId, setSelectOptionId] = useState(optionId[0]);
  const [checkPersonaNo, setSelectCheckPersonaNo] = useState(false);
  const [itemCart, setItemCart] = useState(total);

  const handleAdd = (product, amount) => {
    createCart({
      branchId: id,
      productId: product.id,
      userId: user.id,
      amount,
    });
  };

  const handleMakePayment = (values) => {
    switch (values.method) {
      case "manual":
        createBuyStore({
          branchId: id,
          manualType: selectHacienda.id,
          data: cartsData.map((cart) => ({
            product_id: cart.product.id,
            amount: cart.amount,
            total: cart.price * cart.amount,
            branch_id: id,
          })),
          client_id: currentUser?.id,
          generate_in_treasury: selectHacienda.id,
          identification_type: selectOptionId.id,
          number_id: values.number_id,
          name_client: values.name_client,
          email_client: values.email_client,
          cabys: values.cabys,
          method: values.method,
          direct_debit: values.direct_debit,
        });
        break;

      default:
        createBuyStore({
          branchId: id,
          manualType: selectHacienda.id,
          data: cartsData.map((cart) => ({
            product_id: cart.product.id,
            amount: cart.amount,
            total: cart.price * cart.amount,
            branch_id: id,
          })),
          client_id: currentUser?.id,
          generate_in_treasury: selectHacienda.id,
          identification_type: selectOptionId.id,
          number_id: values.number_id,
          name_client: values.name_client,
          email_client: values.email_client,
          cabys: values.cabys,
          method: values.method,
          direct_debit: values.direct_debit,
        });
        break;
    }
  };

  // Payment sepa debit -> Stripe process
  useEffect(() => {
    if (sepaDebitPaymentResult.isSuccess) {
      dispatch(setPaymentAction("START_PAYMENT_METHOD_X"));

      /*
      stripe.confirmSepaDebitSetup(clientSecret, {
        payment_method: {
          sepa_debit: ibanElement,
          billing_details: {
            name: values.name_client,
            email: values.email_client,
            address: {
              country: values.country, // Asegúrate de tener este valor en tu formulario
              line1: values.address_line1, // Asegúrate de tener este valor en tu formulario
            },
          },
        },
      }).then(function(response) {
        if (response.error) {
          console.error(response.error.message);
          toasts.danger("Error en el proceso de pago", response.error.message);
        } else {
          console.log("Proceso exitoso", response);
          toasts.success("Proceso exitoso", "El proceso de pago fue realizado exitosamente");
          // Aquí puedes realizar cualquier acción adicional, como actualizar el estado de la compra
        }
      });
      */

      /*
      toasts.success(
        "Proceso exitoso",
        "El proceso de pago fué realizado exitosamente"
      );
      */
    }
  }, [sepaDebitPaymentResult]);

  // Payment manual
  useEffect(() => {
    if (stateBuyStore.isSuccess) {
      toasts.success("Compra exitosa", "La compra fué realizada exitosamente");
      setCartVisible(false);
      refetchCarts();
    }

    if (stateBuyStore.isError) {
      toasts.danger("Compra fallida", "Ocurrió un error al realizar la compra");
    }
  }, [stateBuyStore]);

  const [users, setUsers] = useState([]);

  //Traer datos de Users
  const fechtDatos = async (search) => {
    try {
      const response = await fetchUsers({
        search: search.length < 1 ? undefined : search,
        page: 1,
        limit: 20,
        branch: id,
      }).unwrap();

      const arrayUsers = [];
      let objetCompanies = {};

      for (let i = 0; i < response.data.length; i++) {
        objetCompanies = {
          id: response.data[i].id,
          name: response.data[i].name,
          email: response.data[i].email,
        };
        arrayUsers.push(objetCompanies);
      }
      setUsers(arrayUsers);
    } catch (error) {
      console.log(error);
    }
  };

  const transactionInitialValues = {
    name_client: "",
    email_client: "",
    number_id: "",
    cabys: "",
    method: "",
    direct_debit: "",
  };

  const transactionValidationSchema = Yup.object().shape({
    name_client: Yup.string().required("El nombre es obligatorio"),
    email_client: Yup.string()
      .email("Correo inválido")
      .required("El correo es obligatorio"),
    number_id: Yup.string().required(
      "El número de identificación es obligatorio"
    ),
    cabys: Yup.string().required("El código CABYS es obligatorio"),
    method: Yup.string().required("El método es obligatorio"),
    direct_debit: Yup.string().required("El débito directo es obligatorio"),
  });


  useEffect(() => {
    setSelectedUser(user_current);
    setCurrentUser(user_current);
  }, [user_current]);

  return (
    <Fragment>
      <div className="flex-1 overflow-hidden">
        <div className="flex flex-col h-screen pb-20">
          <div className="flex flex-col md:flex-row md:gap-x-2 px-4 bottom pb-2 border-b mt-2">
            <div className="flex flex-col flex-1 gap-4 md:gap-2 md:flex-row md:items-start">
              <div className="col-span-full">
                <InputWithCornerHint
                  label={t("store.views.form.search")}
                  type="text"
                  placeholder={t("store.views.form.search_by_product")}
                  onChange={(event) =>
                    setProductSearch(() => event.target.value)
                  }
                  value={productSearch}
                />
              </div>
              <div className="col-span-full">
                <MultiSelect
                  title={t("store.views.categories")}
                  selectedOptions={categories}
                  setSelectedOptions={setCategories}
                  options={categoriesQuery.data ?? []}
                />
              </div>
            </div>
            <div className="relative flex flex-col md:flex-row md:items-center gap-3 gap-x-2 md:gap-x-2 pt-6 md:pt-0 md:self-end mr-2">
              <a href="#" onClick={handleOpenCart} className="relative">
                <FontAwesomeIcon
                  icon={faCartShopping}
                  className={`w-8 h-8 ${
                    itemCart > 0 ? "text-primary-500 " : "text-gray-400"
                  }`}
                />
                {itemCart > 0 && (
                  <span className="absolute -top-4 -right-4 flex h-5 w-5 items-center justify-center rounded-full bg-pink-500 text-white text-sm p-3 border-white border-4">
                    {itemCart}
                  </span>
                )}
              </a>
            </div>
          </div>
          <div className="flex-1 overflow-y-auto">
            {productsQuery.data?.length == 0 ? (
              <div className="px-5 pt-5">
                <Empty
                  texto={t("store.views.message_no_product")}
                  onClick={() => {
                    onOpenStock();
                    toasts.info(
                      "Agregar productos",
                      "Haz clic en el botón de la derecha con el símbolo +"
                    );
                  }}
                />
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 pb-14 lg:grid-cols-3 xl:grid-cols-4 gap-6 w-full px-4 mt-5">
                {productsQuery.isLoading && (
                  <Fragment>
                    <Product isLoading />
                    <Product isLoading />
                    <Product isLoading />
                    <Product isLoading />
                    <Product isLoading />
                    <Product isLoading />
                    <Product isLoading />
                    <Product isLoading />
                  </Fragment>
                )}
                {productsQuery.data?.map((product: any) => (
                  <Product
                    key={product.id}
                    product={product}
                    onAdd={handleAdd}
                  />
                ))}
              </div>
            )}
          </div>
        </div>

        <Formik
          initialValues={transactionInitialValues}
          validationSchema={transactionValidationSchema}
          onSubmit={(values, formikHelpers) => {
            //formikHelpers.resetForm();
            //setCartVisible(false);
          }}
        >
          {({
            handleChange,
            handleSubmit,
            errors,
            values,
            setFieldValue,
            resetForm,
          }) => (
            <DialogCreate
              open={cartVisible}
              setOpen={handleOpenCart}
              tittle={t("store.views.shopping_cart")}
              tittleButtonCreate={t("store.views.form.pay")}
              tittleButtonCancel={t("store.views.form.cancel")}
              OnclickCreate={() => {
                console.log("VALORES:", values);
                handleMakePayment(values);
                setCartVisible(false);
              }}
              OnclickCancel={handleOpenCart}
              acceptDisabled={selectHacienda.id > 0 ? false : true}
              isLoading={createCartResult.isLoading && stateBuyStore.isLoading}
            >
              <div className="mt-10 lg:mt-0 px-4 mb-4">
                <div className="mt-4 bg-white shadow-sm">
                  <h3 className="sr-only">Items in your cart</h3>
                  <ul role="list" className="divide-y divide-gray-200">
                    {cartsData?.map((cart) => (
                      <ListCart
                        cart={cart}
                        onDelete={(cart) =>
                          deleteCart({
                            branchId: id,
                            id: cart.id,
                            userId: user.id,
                          })
                        }
                        onAmountChange={(cart, amount) => {
                          console.debug(cart, amount);
                          updateCart({
                            branchId: id,
                            id: cart.id,
                            amount: amount,
                            price: cart.price,
                            userId: user.id,
                          });
                        }}
                        onPriceChange={(cart, price) => {
                          console.debug(cart, price);
                          updateCart({
                            branchId: id,
                            id: cart.id,
                            amount: cart.amount,
                            price: price,
                            userId: user.id,
                          });
                        }}
                      />
                    ))}
                  </ul>
                  {cartsData?.length > 0 && (
                    <DescriptionListCart
                      total={total}
                      currency={(cartsData ?? [])[0]?.product?.currency}
                    />
                  )}
                </div>
                {/*  <div className="col-span-full grid grid-cols-1 place-content-center mt-4 px-5">
                  <InputWithSelectHint
                    name="user"
                    tittle={t("store.views.shopping_cart_client")}
                    span={t("general.optional")}
                    disabled={checkPersonaNo}
                    setSelected={(user) => {
                      setSelectedUser(user);
                      setCurrentUser(user);

                      setFieldValue("name_client", user?.name);
                      setFieldValue("email_client", user?.email);
                    }}
                    selected={selectedUser}
                    type="text"
                    onQueryChange={(search) => fechtDatos(search)}
                    options={users}
                  />
                </div> */}
                <div className="col-span-full grid grid-cols-1 place-content-center mt-4 sm:mt-4 px-5">
                  <div className="relative flex items-start">
               {/*      <div className="flex h-6 items-center">
                      <input
                        checked={checkPersonaNo}
                        onChange={() =>
                          setSelectCheckPersonaNo(!checkPersonaNo)
                        }
                        id="comments"
                        name=""
                        type="checkbox"
                        aria-describedby=""
                        className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-600"
                      />
                    </div> */}
                    <div className="ml-3 text-sm leading-6">
                      <label
                        htmlFor="comments"
                        className="font-medium text-gray-900"
                      >
                        {t("store.views.shopping_cart_anonymous")}
                      </label>
                    </div>
                  </div>
                </div>
                <div className="col-span-full grid grid-cols-1 place-content-center mt-4 sm:mt-4 px-5">
                  <Select
                    name="generate_in_treasury"
                    tittle={t("gti.views.generate_in_treasury")}
                    selected={selectHacienda}
                    setSelected={setSelectHacienda}
                    options={optionsHacienda}
                  />
                </div>

                {selectHacienda.id === 2 ? (
                  <>
                    <div className="col-span-full grid grid-cols-1 place-content-center mt-4 sm:mt-4 px-5">
                      <InputWithLinkNextToIt
                        label={t("gti.views.code_cabys")}
                        span={t("general.mandatory")}
                        name="cabys"
                        linkText={`(Lista de codigos)`}
                        linkUrl="https://test.fitmewise.com/excel/cabys.xlsx"
                        onChange={handleChange}
                      />
                    </div>
                    <div className="col-span-full grid grid-cols-1 place-content-center mt-4 sm:mt-4 px-5">
                      <Select
                        name="type_dni"
                        tittle={t("gti.views.type_dni")}
                        span={t("general.mandatory")}
                        selected={selectOptionId}
                        setSelected={setSelectOptionId}
                        options={optionId}
                      />
                    </div>
                    <div className="col-span-full grid grid-cols-1 place-content-center mt-4 sm:mt-4 px-5">
                      <InputWithCornerHint
                        label={t("gti.views.nro_dni")}
                        span={t("general.mandatory")}
                        type="number"
                        name="number_id"
                        onChange={handleChange}
                        value={values.number_id}
                      />
                    </div>
                    {checkPersonaNo ? (
                      <>
                        <div className="col-span-full grid grid-cols-1 place-content-center mt-4 sm:mt-4 px-5">
                          <InputWithCornerHint
                            label={t("gti.views.name_client")}
                            span={t("general.mandatory")}
                            type="text"
                            name="name_client"
                            onChange={handleChange}
                            value={values.name_client}
                          />
                        </div>
                        <div className="col-span-full grid grid-cols-1 place-content-center mt-4 sm:mt-4 px-5">
                          <InputWithCornerHint
                            label={t("gti.views.email_client")}
                            span={t("general.mandatory")}
                            type="text"
                            name="email_client"
                            onChange={handleChange}
                            value={values.email_client}
                          />
                        </div>
                      </>
                    ) : (
                      <></>
                    )}
                  </>
                ) : (
                  <>
                    {selectHacienda.id === 3 ? (
                      <>
                        <div className="col-span-full grid grid-cols-1 place-content-center mt-4 sm:mt-4 px-5">
                          <Select
                            name="type_dni"
                            tittle={t("gti.views.type_dni")}
                            selected={selectOptionId}
                            setSelected={setSelectOptionId}
                            options={optionId}
                          />
                        </div>
                        <div className="col-span-full grid grid-cols-1 place-content-center mt-4 sm:mt-4 px-5">
                          <InputWithCornerHint
                            label={t("gti.views.nro_dni")}
                            type="number"
                            name="number_id"
                            onChange={handleChange}
                            value={values.number_id}
                          />
                        </div>
                        {checkPersonaNo ? (
                          <>
                            <div className="col-span-full grid grid-cols-1 place-content-center mt-4 sm:mt-4 px-5">
                              <InputWithCornerHint
                                label={t("gti.views.name_client")}
                                type="text"
                                name="name_client"
                                onChange={handleChange}
                                value={values.name_client}
                              />
                            </div>
                            <div className="col-span-full grid grid-cols-1 place-content-center mt-4 sm:mt-4 px-5">
                              <InputWithCornerHint
                                label={t("gti.views.email_client")}
                                type="text"
                                name="email_client"
                                onChange={handleChange}
                                value={values.email_client}
                              />
                            </div>
                          </>
                        ) : (
                          <></>
                        )}
                      </>
                    ) : (
                      <></>
                    )}
                  </>
                )}
              </div>

              {selectHacienda?.id > 0 && (
                <div className="mt-10 lg:mt-0 px-4 mb-4">
                  <PaymentForm
                    user={currentUser}
                    clientSecret={stripeSetupIntentResult?.data?.clientSecret}
                    publicKey={paymentConfigQuery?.data?.publicKeyPayment}
                    selectMethod={(method) => {
                      setFieldValue("method", method);

                      if (method === "manual") {
                        setFieldValue("direct_debit", false);
                        setFieldValue("manual_type", 1);
                      }
                    }}
                    selectManualType={(manualType) => {
                      setFieldValue("manual_type", manualType);
                    }}
                  />
                </div>
              )}

           
            </DialogCreate>
          )}
        </Formik>
      </div>
    </Fragment>
  );
};
